/** Reactive, IndexedDB-backed pattern library. Holds an in-memory mirror for
 *  synchronous reads (React/useSyncExternalStore) and writes through to IDB.
 *  The data layer is deliberately self-contained so a Cloudflare sync adapter
 *  can later mirror these same records to D1/KV/R2 behind the same API. */
import { openDB, type IDBPDatabase } from 'idb';
import type {
  ConflictChoice,
  PatternRecord,
  PatternStatus,
  Settings,
} from '../domain/types';
import {
  SCHEMA,
  analyzeImport,
  duplicateRecord,
  isExpiredTrash,
  makeBundle,
  normalize,
  nowISO,
  resolveImport,
  trashDaysLeft,
} from './model';

const DB_NAME = 'skein';
const DB_VERSION = 1;
const PATTERNS = 'patterns';
const KV = 'kv';
const SETTINGS_KEY = 'settings';

const DEFAULT_SETTINGS: Settings = { theme: 'auto', activePatternId: null, lastGauge: 'dk' };

type Listener = () => void;

export class Library {
  private db: IDBPDatabase | null = null;
  private patterns = new Map<string, PatternRecord>();
  private settings: Settings = { ...DEFAULT_SETTINGS };
  private listeners = new Set<Listener>();
  private ready = false;
  /** A snapshot identity that changes on every mutation (for useSyncExternalStore). */
  private version = 0;

  async init(): Promise<void> {
    if (this.ready) return;
    this.db = await openDB(DB_NAME, DB_VERSION, {
      upgrade(db) {
        if (!db.objectStoreNames.contains(PATTERNS)) db.createObjectStore(PATTERNS, { keyPath: 'id' });
        if (!db.objectStoreNames.contains(KV)) db.createObjectStore(KV);
      },
    });
    const all = (await this.db.getAll(PATTERNS)) as PatternRecord[];
    this.patterns = new Map(all.map((p) => [p.id, normalize(p)]));
    const s = (await this.db.get(KV, SETTINGS_KEY)) as Settings | undefined;
    this.settings = { ...DEFAULT_SETTINGS, ...(s || {}) };
    await this.purgeExpiredTrash();
    this.ready = true;
    this.bump();
    // best-effort: ask the browser not to evict our offline-first data
    try {
      await navigator.storage?.persist?.();
    } catch {
      /* ignore */
    }
  }

  /** Close the underlying IndexedDB connection (so the DB can be deleted). */
  close() {
    this.db?.close();
    this.db = null;
    this.ready = false;
  }

  /* ----------------------------- reactivity ----------------------------- */
  subscribe = (fn: Listener) => {
    this.listeners.add(fn);
    return () => this.listeners.delete(fn);
  };
  getVersion = () => this.version;
  private bump() {
    this.version++;
    this.listeners.forEach((fn) => fn());
  }

  /* ------------------------------- writes ------------------------------- */
  private async put(rec: PatternRecord) {
    this.patterns.set(rec.id, rec);
    await this.db?.put(PATTERNS, rec);
    this.bump();
  }
  private async del(id: string) {
    this.patterns.delete(id);
    await this.db?.delete(PATTERNS, id);
    this.bump();
  }

  async create(partial: Partial<PatternRecord>): Promise<PatternRecord> {
    const rec = normalize({ ...partial, createdAt: nowISO(), updatedAt: nowISO() });
    await this.put(rec);
    return rec;
  }
  get(id: string | null | undefined): PatternRecord | null {
    return (id && this.patterns.get(id)) || null;
  }
  async update(id: string, patch: Partial<PatternRecord>, touch = true): Promise<PatternRecord | null> {
    const cur = this.patterns.get(id);
    if (!cur) return null;
    const next = { ...cur, ...patch, updatedAt: touch ? nowISO() : cur.updatedAt };
    await this.put(next);
    return next;
  }
  list(status?: PatternStatus): PatternRecord[] {
    const all = [...this.patterns.values()];
    const filtered = status ? all.filter((p) => p.status === status) : all;
    return filtered.sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  }
  counts(): Record<PatternStatus, number> {
    const c: Record<PatternStatus, number> = { active: 0, archived: 0, trashed: 0 };
    for (const p of this.patterns.values()) c[p.status]++;
    return c;
  }
  async duplicate(id: string, newName?: string): Promise<PatternRecord | null> {
    const src = this.patterns.get(id);
    if (!src) return null;
    const copy = duplicateRecord(src, newName);
    await this.put(copy);
    return copy;
  }
  archive = (id: string) => this.update(id, { status: 'archived', archivedAt: nowISO() });
  unarchive = (id: string) => this.update(id, { status: 'active', archivedAt: null });
  trash = (id: string) => this.update(id, { status: 'trashed', trashedAt: nowISO() });
  restore = (id: string) => this.update(id, { status: 'active', trashedAt: null, archivedAt: null });
  removeForever = (id: string) => this.del(id);
  trashDaysLeft = trashDaysLeft;

  async purgeExpiredTrash(): Promise<number> {
    let n = 0;
    for (const p of [...this.patterns.values()]) {
      if (isExpiredTrash(p)) {
        this.patterns.delete(p.id);
        await this.db?.delete(PATTERNS, p.id);
        n++;
      }
    }
    if (n) this.bump();
    return n;
  }

  /* ------------------------------ settings ------------------------------ */
  getSettings = (): Settings => this.settings;
  async setSetting<K extends keyof Settings>(key: K, value: Settings[K]) {
    this.settings = { ...this.settings, [key]: value };
    await this.db?.put(KV, this.settings, SETTINGS_KEY);
    this.bump();
  }

  /* --------------------------- import / export -------------------------- */
  exportPattern(id: string) {
    const rec = this.get(id);
    return rec ? makeBundle('pattern', [rec]) : null;
  }
  exportLibrary(includeTrash = false) {
    const pats = [...this.patterns.values()].filter((p) => includeTrash || p.status !== 'trashed');
    return makeBundle('library', pats);
  }
  analyze(text: string) {
    return analyzeImport(text, (id) => this.get(id));
  }
  async applyImport(items: ReturnType<Library['analyze']>['items'], decisions: Record<string, ConflictChoice>) {
    const res = resolveImport(items, decisions);
    for (const rec of res.toPut) await this.put(rec);
    return res;
  }
}

export const SCHEMA_VERSION = SCHEMA;
